import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { createCompanyWithSetup, suggestTemplates } from '../../reducers/companies';
import './NewCompanyPage.css';

// Quick-pick trades — clicking one pre-fills a good description for the AI.
const TRADES = [
  { label: 'Electrician', icon: 'fa-bolt', desc: 'Electrical contractor doing installations, repairs and COC certificates' },
  { label: 'Plumber', icon: 'fa-wrench', desc: 'Plumbing and maintenance business handling call-outs and installations' },
  { label: 'Vet / Animal', icon: 'fa-paw', desc: 'Veterinary / animal physio practice treating animals like horses, dogs and cats' },
  { label: 'Farrier', icon: 'fa-horse', desc: 'Farrier shoeing horses, with multiple horses kept under each owner' },
  { label: 'Courier', icon: 'fa-truck', desc: 'Courier and delivery service tracking parcels from pickup to delivery' },
  { label: 'Cleaning', icon: 'fa-broom', desc: 'Cleaning service with scheduled jobs and recurring contracts' },
  { label: 'Property', icon: 'fa-home', desc: 'Property rental agency managing leases, applications and inspections' },
  { label: 'Beauty / Salon', icon: 'fa-spa', desc: 'Beauty salon offering bookable services and packages' },
];

const STEPS = ['Your company', 'What you do', 'Your flows'];

class NewCompanyPage extends Component {
  state = {
    step: 1,
    companyName: '',
    businessDescription: '',
    activeTrade: null,
    suggesting: false,
    suggestError: null,
    suggestions: null,
    enabledEntityTemplates: [],
    enabledAppointmentTypes: [],
    refinement: '',
    refining: false,
    creating: false,
  };

  handleNext = () => {
    if (!this.state.companyName.trim()) return;
    this.setState({ step: 2, suggestError: null });
  };

  selectTrade = (t) => {
    this.setState({ businessDescription: t.desc, activeTrade: t.label, suggestError: null });
  };

  handleGenerate = async () => {
    const { businessDescription } = this.state;
    if (!businessDescription.trim()) return;
    this.setState({ suggesting: true, suggestError: null });
    try {
      const result = await this.props.dispatchSuggestTemplates(businessDescription.trim());
      if (result && result.entityTemplates) {
        this.setState({
          suggestions: result,
          enabledEntityTemplates: result.entityTemplates.map(() => true),
          enabledAppointmentTypes: (result.appointmentTypes || []).map(() => true),
          step: 3,
          suggesting: false,
        });
      } else {
        this.setState({ suggestError: 'Could not generate your setup. Please try again.', suggesting: false });
      }
    } catch (e) {
      this.setState({ suggestError: 'Error generating your setup. Please try again.', suggesting: false });
    }
  };

  handleSkip = () => this.handleCreate(true);

  handleRefine = async () => {
    const { businessDescription, suggestions, refinement } = this.state;
    if (!refinement.trim()) return;
    this.setState({ refining: true });
    try {
      const result = await this.props.dispatchSuggestTemplates(businessDescription.trim(), suggestions, refinement.trim());
      if (result && result.entityTemplates) {
        this.setState({
          suggestions: result,
          enabledEntityTemplates: result.entityTemplates.map(() => true),
          enabledAppointmentTypes: (result.appointmentTypes || []).map(() => true),
          refinement: '',
          refining: false,
        });
      } else {
        this.setState({ refining: false });
      }
    } catch (e) {
      this.setState({ refining: false });
    }
  };

  handleCreate = async (skipTemplates = false) => {
    const { companyName, suggestions, enabledEntityTemplates, enabledAppointmentTypes } = this.state;
    const company = { companyName: companyName.trim() };
    let templates = null;
    if (!skipTemplates && suggestions) {
      templates = {
        entityTemplates: suggestions.entityTemplates.filter((_, i) => enabledEntityTemplates[i]),
        invoiceTemplate: suggestions.invoiceTemplate,
        appointmentTypes: (suggestions.appointmentTypes || []).filter((_, i) => enabledAppointmentTypes[i]),
      };
    }
    this.setState({ creating: true });
    try {
      await this.props.dispatchCreateCompanyWithSetup(company, templates);
      this.props.history.push('/');
    } catch (e) {
      this.setState({ creating: false });
    }
  };

  toggleEntityTemplate = (i) => {
    const enabled = [...this.state.enabledEntityTemplates];
    enabled[i] = !enabled[i];
    this.setState({ enabledEntityTemplates: enabled });
  };

  toggleAppointmentType = (i) => {
    const enabled = [...this.state.enabledAppointmentTypes];
    enabled[i] = !enabled[i];
    this.setState({ enabledAppointmentTypes: enabled });
  };

  renderStepper() {
    const { step } = this.state;
    return (
      <ol className="ncp-stepper">
        {STEPS.map((label, i) => (
          <li key={i} className={`${step > i + 1 ? 'done' : ''} ${step === i + 1 ? 'active' : ''}`}>
            <span className="ncp-step-dot">{step > i + 1 ? <i className="fas fa-check" /> : i + 1}</span>
            <span className="ncp-step-name">{label}</span>
          </li>
        ))}
      </ol>
    );
  }

  renderStep1() {
    const { companyName } = this.state;
    return (
      <div className="ncp-card">
        <div className="ncp-kicker">Let's set you up</div>
        <h2>What's your company called?</h2>
        <p className="ncp-sub">This is how it appears on invoices and to your clients.</p>
        <label className="ncp-field">
          <i className="fas fa-building" />
          <input
            type="text"
            placeholder="Company name"
            value={companyName}
            onChange={e => this.setState({ companyName: e.target.value })}
            onKeyDown={e => e.key === 'Enter' && this.handleNext()}
            autoFocus
          />
        </label>
        <div className="ncp-actions">
          <Link to="/" className="ncp-btn ncp-btn-ghost">Cancel</Link>
          <button type="button" className="ncp-btn ncp-btn-primary" onClick={this.handleNext} disabled={!companyName.trim()}>
            Next <i className="fas fa-arrow-right" />
          </button>
        </div>
      </div>
    );
  }

  renderStep2() {
    const { companyName, businessDescription, activeTrade, suggesting, suggestError } = this.state;
    return (
      <div className="ncp-card">
        <div className="ncp-kicker">{companyName}</div>
        <h2>What do you do?</h2>
        <p className="ncp-sub">
          Pick your trade or describe your business. MoneyApp's AI then builds your starter
          workflows — appointment types, statuses, client fields and invoice — tailored to you.
        </p>

        <div className="ncp-trades">
          {TRADES.map(t => (
            <button
              type="button"
              key={t.label}
              className={`ncp-trade ${activeTrade === t.label ? 'active' : ''}`}
              onClick={() => this.selectTrade(t)}
            >
              <i className={`fas ${t.icon}`} />
              {t.label}
            </button>
          ))}
        </div>

        <label className="ncp-field ncp-field-area">
          <textarea
            rows="3"
            placeholder="e.g. veterinary practice that treats dogs, cats and horses"
            value={businessDescription}
            onChange={e => this.setState({ businessDescription: e.target.value, activeTrade: null })}
          />
        </label>

        {suggestError && <div className="ncp-alert"><i className="fas fa-exclamation-circle" /> {suggestError}</div>}

        <div className="ncp-actions">
          <button type="button" className="ncp-btn ncp-btn-ghost" onClick={() => this.setState({ step: 1 })}>
            <i className="fas fa-arrow-left" /> Back
          </button>
          <div className="ncp-actions-right">
            <button type="button" className="ncp-btn ncp-btn-text" onClick={this.handleSkip} disabled={suggesting}>
              Skip for now
            </button>
            <button type="button" className="ncp-btn ncp-btn-primary" onClick={this.handleGenerate} disabled={!businessDescription.trim() || suggesting}>
              {suggesting
                ? <><i className="fas fa-circle-notch fa-spin" /> Generating…</>
                : <><i className="fas fa-magic" /> Generate my setup</>}
            </button>
          </div>
        </div>
      </div>
    );
  }

  renderFlowCard(apt, i, enabled) {
    return (
      <button
        type="button"
        key={i}
        className={`ncp-flow ${enabled ? 'on' : ''}`}
        onClick={() => this.toggleAppointmentType(i)}
      >
        <span className="ncp-check"><i className={`fas ${enabled ? 'fa-check-circle' : 'fa-circle'}`} /></span>
        <div className="ncp-flow-body">
          <div className="ncp-flow-name">{apt.name}</div>
          {(apt.reviewDescription || apt.description) && (
            <div className="ncp-flow-desc">{apt.reviewDescription || apt.description}</div>
          )}
          {(apt.statuses || []).length > 0 && (
            <div className="ncp-pills">
              {apt.statuses.map((s, k) => (
                <span className="ncp-pill" key={k}>
                  <span className="ncp-pill-dot" style={{ background: s.color || '#0DA1B2' }} />
                  {s.name}
                </span>
              ))}
            </div>
          )}
          {(apt.components || []).length > 0 && (
            <div className="ncp-fields">
              {apt.components.map((c, k) => (
                <span className="ncp-fieldchip" key={k}><i className="fas fa-plus" /> {c.label || c.prop || c.type}</span>
              ))}
            </div>
          )}
        </div>
      </button>
    );
  }

  renderStep3() {
    const { suggestions, enabledEntityTemplates, enabledAppointmentTypes, refinement, refining, creating } = this.state;
    if (!suggestions) return null;
    const { entityTemplates = [], invoiceTemplate, appointmentTypes = [] } = suggestions;

    return (
      <div className="ncp-card ncp-card-wide">
        <div className="ncp-kicker">Built by AI — yours to tweak</div>
        <h2>Your starter setup</h2>
        <p className="ncp-sub">Keep what fits, switch off what doesn't. You can edit everything later in settings.</p>

        {appointmentTypes.length > 0 && (
          <div className="ncp-group">
            <h5><i className="fas fa-project-diagram" /> Workflows (appointment types)</h5>
            <div className="ncp-flow-grid">
              {appointmentTypes.map((apt, i) => this.renderFlowCard(apt, i, enabledAppointmentTypes[i]))}
            </div>
          </div>
        )}

        {entityTemplates.length > 0 && (
          <div className="ncp-group">
            <h5><i className="fas fa-users" /> Client &amp; record types</h5>
            <div className="ncp-flow-grid">
              {entityTemplates.map((et, i) => {
                const enabled = enabledEntityTemplates[i];
                return (
                  <button type="button" key={i} className={`ncp-flow ${enabled ? 'on' : ''}`} onClick={() => this.toggleEntityTemplate(i)}>
                    <span className="ncp-check"><i className={`fas ${enabled ? 'fa-check-circle' : 'fa-circle'}`} /></span>
                    <div className="ncp-flow-body">
                      <div className="ncp-flow-name">
                        {et.reviewName || et.name || `Type ${i + 1}`}
                        {et.secondary && <span className="ncp-badge">nested</span>}
                      </div>
                      {et.reviewDescription && <div className="ncp-flow-desc">{et.reviewDescription}</div>}
                      <div className="ncp-fields">
                        {(et.descriptions || []).map((d, k) => (
                          <span className="ncp-fieldchip" key={k}><i className="fas fa-plus" /> {d.description}</span>
                        ))}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {invoiceTemplate && (
          <div className="ncp-group">
            <h5><i className="fas fa-file-invoice-dollar" /> Invoice</h5>
            <div className="ncp-flow on always">
              <span className="ncp-check"><i className="fas fa-check-circle" /></span>
              <div className="ncp-flow-body">
                <div className="ncp-flow-name">{invoiceTemplate.templateName || 'Standard Invoice'}</div>
                <div className="ncp-flow-desc">Always included — branded and ready to send.</div>
              </div>
            </div>
          </div>
        )}

        <div className="ncp-refine">
          <i className="fas fa-magic ncp-refine-icon" />
          <input
            type="text"
            placeholder="Want to add something? e.g. add a sign-off sheet to call-outs"
            value={refinement}
            onChange={e => this.setState({ refinement: e.target.value })}
            onKeyDown={e => e.key === 'Enter' && this.handleRefine()}
          />
          <button type="button" className="ncp-btn ncp-btn-soft" onClick={this.handleRefine} disabled={!refinement.trim() || refining}>
            {refining ? <i className="fas fa-circle-notch fa-spin" /> : 'Add'}
          </button>
        </div>

        <div className="ncp-actions">
          <button type="button" className="ncp-btn ncp-btn-ghost" onClick={() => this.setState({ step: 2 })}>
            <i className="fas fa-arrow-left" /> Back
          </button>
          <button type="button" className="ncp-btn ncp-btn-primary" onClick={() => this.handleCreate(false)} disabled={creating}>
            {creating
              ? <><i className="fas fa-circle-notch fa-spin" /> Creating…</>
              : <><i className="fas fa-rocket" /> Create my business</>}
          </button>
        </div>
      </div>
    );
  }

  render() {
    const { step } = this.state;
    return (
      <div className="ncp">
        <span className="ncp-orb ncp-orb-1" />
        <span className="ncp-orb ncp-orb-2" />
        <div className="ncp-inner">
          <Link to="/" className="ncp-brand" aria-label="MoneyApp home">
            <img src="/moneyapp_blue.png" alt="MoneyApp" />
          </Link>
          {this.renderStepper()}
          {step === 1 && this.renderStep1()}
          {step === 2 && this.renderStep2()}
          {step === 3 && this.renderStep3()}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({ user: state.auth.user });
const mapDispatchToProps = (dispatch) => ({
  dispatchCreateCompanyWithSetup: (company, templates) => dispatch(createCompanyWithSetup(company, templates)),
  dispatchSuggestTemplates: (description, existing, refinement) => dispatch(suggestTemplates(description, existing, refinement)),
});

export default connect(mapStateToProps, mapDispatchToProps)(NewCompanyPage);
